import {api} from '../../../shared';
import {
  BenefactorModel,
  BenefactorsFilter,
  BenefactorWithMetaModel,
  CreateBenefactorDTO,
  EditBenefactorNamesDTO,
  EditBenefactorNamesParams,
  EditBenefactorEmailParams,
  EditBenefactorEmailDTO,
  EditBenefactorPhoneParams,
  EditBenefactorPhoneDTO,
  BenefactorOrderWithMetaModel,
  BenefactorOrdersFilter,
  OrderDetailModel,
  BenefactorPaymentsInfoModel,
  BenefactorSubscriptionsWithMetaModel,
  BenefactorSubscriptionsFilter,
  BenefactorScoringModel,
  PointsAssignmentWithMetaModel,
  PointsAssignmentFilter,
} from '../model';

export const benefactorsApi = {
  getBenefactors: async (params: BenefactorsFilter = {}): Promise<BenefactorWithMetaModel> => {
    const {pageSize = 10, after, ...restParams} = params;
    const queryParams = {
      ...restParams,
      includeTotalCount: true,
      first: pageSize,
      ...(after && {after}),
    };

    const {data} = await api.get<BenefactorWithMetaModel>('/api/iam/admin/benefactors', {params: queryParams});
    return data;
  },

  getBenefactorOrders: async (
    benefactorId: string,
    params: BenefactorOrdersFilter = {},
  ): Promise<BenefactorOrderWithMetaModel> => {
    const {pageSize = 10, after, ...restParams} = params;
    const queryParams = {
      ...restParams,
      includeTotalCount: true,
      first: pageSize,
      ...(after && {after}),
    };

    const {data} = await api.get<BenefactorOrderWithMetaModel>(
      `/api/finance/public/benefactors/${benefactorId}/orders`,
      {params: queryParams},
    );
    return data;
  },

  getOrderById: async (orderId: string): Promise<OrderDetailModel> => {
    const {data} = await api.get<OrderDetailModel>(`/api/finance/public/orders/${orderId}`);
    return data;
  },

  createBenefactor: async (benefactor: CreateBenefactorDTO): Promise<BenefactorModel> => {
    const {data} = await api.post<BenefactorModel>('/api/iam/admin/benefactors', benefactor);
    return data;
  },
  getBenefactorById: async (benefactorId: string): Promise<BenefactorModel> => {
    const {data} = await api.get<BenefactorModel>(`/api/iam/admin/benefactors/${benefactorId}`);
    return data;
  },
  updateBenefactorNames: async ({
    benefactorId,
    displayName,
    realName,
  }: EditBenefactorNamesParams): Promise<BenefactorModel> => {
    const body: EditBenefactorNamesDTO = {displayName, realName};
    const {data} = await api.put<BenefactorModel>(`/api/iam/admin/benefactors/${benefactorId}/names`, body);
    return data;
  },
  updateBenefactorEmail: async ({benefactorId, email}: EditBenefactorEmailParams): Promise<BenefactorModel> => {
    const body: EditBenefactorEmailDTO = {email};
    const {data} = await api.put<BenefactorModel>(`/api/iam/admin/benefactors/${benefactorId}/email`, body);
    return data;
  },
  updateBenefactorPhone: async ({benefactorId, phoneNumber}: EditBenefactorPhoneParams): Promise<BenefactorModel> => {
    const body: EditBenefactorPhoneDTO = {phoneNumber};
    const {data} = await api.put<BenefactorModel>(`/api/iam/admin/benefactors/${benefactorId}/phone`, body);
    return data;
  },
  deleteBenefactor: async (benefactorId: string): Promise<void> => {
    const {data} = await api.delete<void>(`/api/iam/admin/benefactors/${benefactorId}`);
    return data;
  },

  getBenefactorPaymentsInfo: async (benefactorId: string): Promise<BenefactorPaymentsInfoModel[]> => {
    const {data} = await api.get<BenefactorPaymentsInfoModel[]>(
      `/api/finance/public/benefactors/${benefactorId}/retained-payment-methods`,
    );
    return data;
  },

  getBenefactorSubscriptions: async (
    benefactorId: string,
    params: BenefactorSubscriptionsFilter = {filter: 'All' as any},
  ): Promise<BenefactorSubscriptionsWithMetaModel> => {
    const queryParams = {
      filter: params.filter,
      ...(params.first && {first: params.first}),
      ...(params.after && {after: params.after}),
      ...(params.last && {last: params.last}),
      ...(params.before && {before: params.before}),
      ...(params.includeTotalCount !== undefined && {includeTotalCount: params.includeTotalCount}),
    };

    const {data} = await api.get<BenefactorSubscriptionsWithMetaModel>(
      `/api/finance/public/benefactors/${benefactorId}/subscriptions`,
      {params: queryParams},
    );
    return data;
  },

  getBenefactorScoring: async (benefactorId: string): Promise<BenefactorScoringModel> => {
    const {data} = await api.get<BenefactorScoringModel>(`/api/scoring/admin/benefactors/${benefactorId}`);
    return data;
  },

  getBenefactorPointsAssignment: async (
    benefactorId: string,
    params: PointsAssignmentFilter = {},
  ): Promise<PointsAssignmentWithMetaModel> => {
    const {pageSize = 25, after, ...restParams} = params;
    const queryParams = {
      ...restParams,
      first: pageSize,
      ...(after && {after}),
    };

    const {data} = await api.get<PointsAssignmentWithMetaModel>(
      `/api/scoring/admin/benefactors/${benefactorId}/points-assignment`,
      {params: queryParams},
    );
    return data;
  },
};
